import React, { createContext, useContext, useState, useRef } from 'react';
import { ConfirmationDialogOptions } from '@components/confirmation/ConfirmationModal.types';
import ConfirmationModal from '@components/confirmation/ConfirmationModal';
import { ConfirmationServiceProviderProps } from './Confirmation.types';

const ConfirmationContext = createContext<(options: ConfirmationDialogOptions, callback?: unknown) => Promise<void> | any>(Promise.reject);

export const useConfirmation = () => {
  const confirm = useContext(ConfirmationContext);
  return { confirm };
};

export const ConfirmationProvider = (props: ConfirmationServiceProviderProps) => {
  const { children } = props;
  const [confirmationState, setConfirmationState] = useState<ConfirmationDialogOptions | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const awaitingPromiseRef = useRef<{
    resolve: () => void;
    reject: () => void;
    callback: () => void;
  }>();

  const openConfirmation = (options: ConfirmationDialogOptions, callback) => {
    setConfirmationState(options);
    setIsOpen(true);

    return new Promise<void>((resolve, reject) => {
      awaitingPromiseRef.current = { resolve, reject, callback };
    });
  };

  const handleClose = async () => {
    if (confirmationState && confirmationState.catchOnCancel && awaitingPromiseRef.current) {
      awaitingPromiseRef.current.reject();
    }
    setIsOpen(false);
  };

  const handleSubmit = async () => {
    if (awaitingPromiseRef.current) {
      if (awaitingPromiseRef.current.callback) {
        try {
          setIsLoading(true);
          await awaitingPromiseRef.current.callback();
          setIsLoading(false);
        } catch (err) {
          setIsLoading(false);
          return null;
        }
      }
      awaitingPromiseRef.current.resolve();
    }
    setIsOpen(false);
  };

  return (
    <>
      <ConfirmationContext.Provider value={openConfirmation}>{children}</ConfirmationContext.Provider>
      <ConfirmationModal isOpen={isOpen} onConfirm={handleSubmit} onClose={handleClose} isLoading={isLoading} {...confirmationState} />
    </>
  );
};
